import {
  SideNavigationV2Props,
  PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { ButtonProps } from "skedyul-ui/ui";
import { store, type RootState } from "@store/index";
import type { ProvisionStep } from "skedyul-core/features/deployments/resources/types";
import type { LogViewerEntry } from "skedyul-ui/ui";
import { trpc } from "@utils/api";
import { clearPendingMigration } from "@store/features/provisions/provisionsSlice";

type ProvisionLogsParams = {
  provisionId: string;
};

// Map provision step status to log level
const mapStepStatusToLevel = (
  status: ProvisionStep["status"],
): LogViewerEntry["level"] => {
  switch (status) {
    case "failed":
      return "error";
    case "awaiting_approval":
      return "warn";
    default:
      return "info";
  }
};

// Convert provision steps to log entries
const stepsToLogEntries = (steps: ProvisionStep[]): LogViewerEntry[] => {
  return steps.map((step, index) => ({
    id: `step-${index}`,
    timestamp: step.startedAt || new Date().toISOString(),
    message: `${step.name}${step.status === "in_progress" ? "..." : step.status === "completed" ? " ✓" : step.status === "failed" ? ` ✗ ${step.errorMessage || ""}` : step.status === "awaiting_approval" ? " (awaiting approval)" : ""}`,
    level: mapStepStatusToLevel(step.status),
  }));
};

// Get status message for display
const getStatusMessage = (
  status: string | undefined,
  currentStep: string | null | undefined,
): string => {
  if (!status) return "Loading...";

  switch (status) {
    case "PENDING":
      return "Initializing...";
    case "STARTING":
      return currentStep || "Starting compute infrastructure...";
    case "HEALTH_CHECK":
      return currentStep || "Verifying health...";
    case "ASSIGNING":
      return currentStep || "Assigning to version...";
    case "SYNCING_TOOLS":
      return currentStep || "Syncing tools and webhooks...";
    case "SYNCING_RESOURCES":
      return currentStep || "Syncing app resources...";
    case "AWAITING_APPROVAL":
      return currentStep || "Awaiting migration approval...";
    case "PROVISION_HANDLER":
      return currentStep || "Running provision handler...";
    case "STOPPING_LEGACY":
      return currentStep || "Cleaning up previous executable...";
    case "COMPLETED":
      return "Provisioning complete";
    case "FAILED":
      return "Provisioning failed";
    default:
      return currentStep || "Processing...";
  }
};

export const provisionLogsSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: ProvisionLogsParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const { sideNavigation } = args.state;
  const paramsProvisionId = sideNavigation[args.position]?.params
    ?.provisionId as string | undefined;
  const provisionId = args.props.provisionId || paramsProvisionId || "";

  const isOpen = sideNavigation[args.position]?.isOpen ?? true;

  // Get provision data from Redux state
  const fullState = args.state as RootState;
  const stateProvision = fullState.provisions?.provision?.data ?? null;
  const statePendingMigration = fullState.provisions?.pendingMigration ?? null;

  // Also check store.getState() for the most current data (for real-time updates)
  const liveState = store.getState();
  const liveProvision = liveState.provisions?.provision?.data ?? null;
  const livePendingMigration = liveState.provisions?.pendingMigration ?? null;

  // Use live state if available (for real-time updates), otherwise fall back to passed state
  const provision = liveProvision ?? stateProvision;
  const pendingMigration = livePendingMigration ?? statePendingMigration;

  // Only use data if it matches the current provisionId
  const matchesCurrentId = provision?.id === provisionId;
  const currentProvision = matchesCurrentId ? provision : null;
  const currentPendingMigration = matchesCurrentId ? pendingMigration : null;

  // Convert steps to log entries
  const provisionSteps = currentProvision?.steps ?? [];
  const logEntries: LogViewerEntry[] = stepsToLogEntries(provisionSteps);

  // Add pending migration approval block if present
  if (currentPendingMigration) {
    logEntries.push({
      id: `approval-${currentPendingMigration.migrationId}`,
      timestamp: currentPendingMigration.createdAt,
      message: "Migration requires admin approval",
      level: "warn",
      type: "approval_required",
      approvalData: {
        migrationId: currentPendingMigration.migrationId,
        impacts: currentPendingMigration.impacts,
        timeoutMinutes: currentPendingMigration.timeoutMinutes,
        createdAt: currentPendingMigration.createdAt,
      },
    });
  }

  // Build title from provision info
  const appName = currentProvision?.appName ?? "";
  const appVersionName = currentProvision?.appVersionName ?? "";

  // Build a descriptive title
  let title = "Provisioning";
  if (appName && appVersionName) {
    title = `${appName} - ${appVersionName}`;
  } else if (appName) {
    title = appName;
  }

  // Build subtitle from status
  const subtitle = getStatusMessage(
    currentProvision?.status,
    currentProvision?.currentStep,
  );

  // Build logs URL if we have the necessary IDs
  const subdomain = args.routerContext.params?.subdomain;
  const appId = currentProvision?.appId;
  const appVersionId = currentProvision?.appVersionId;

  // Build the logs URL with app and appVersion filters
  let logsUrl: string | undefined;
  if (subdomain && appId && appVersionId) {
    logsUrl = `/${subdomain}/developers/logs?app=${appId}&appVersion=${appVersionId}`;
  }

  // Build actions array with View Logs link if available
  const actions: ButtonProps[] = [];
  if (logsUrl) {
    actions.push({
      label: "View App Logs",
      leftIcon: "FileText",
      variant: "outline",
      size: "sm",
      href: logsUrl,
    });
  }

  // Approval handlers
  const handleApproveMigration = async (migrationId: string) => {
    try {
      await trpc.crm.approveMigration.mutate({ migrationId });
      store.dispatch(clearPendingMigration());
    } catch (err) {
      console.error("Failed to approve migration:", err);
    }
  };

  const handleDenyMigration = async (migrationId: string) => {
    try {
      await trpc.crm.denyMigration.mutate({ migrationId });
      store.dispatch(clearPendingMigration());
    } catch (err) {
      console.error("Failed to deny migration:", err);
    }
  };

  return {
    side: args.position,
    isOpen,
    width: args.width || "600px",
    onSidebarOpenChange: (open: boolean) => {
      if (!open) {
        const { pathname, search } = window.location;
        const params = new URLSearchParams(search);
        params.delete("provision");
        const newSearch = params.toString();
        args.routerContext.router.replace(
          `${pathname}${newSearch ? `?${newSearch}` : ""}`,
        );
      }
    },
    component: {
      component: "PageV2",
      props: {
        title,
        hideTitle: false,
        content: {
          structure: "sidebar",
          components: [
            {
              type: "LogViewer",
              title: subtitle,
              props: {
                logs: logEntries,
                showTimestamps: true,
                showLevels: true,
                emptyMessage: "Waiting for provision logs...",
                onApprove: handleApproveMigration,
                onDeny: handleDenyMigration,
              },
            },
          ],
        },
      },
    },
  };
};
